/**
 * EventCardPrize — the "Prize: $X" chip on an event card.
 *
 * Gated by the "display prize to contestants" toggle for BOTH fixed and
 * dynamic events — toggle off shows nothing. When on: dynamic shows the LIVE
 * computed pool (updates with entries); fixed shows its static prize.
 */

import { Trophy } from 'lucide-react';
import { useEventPrize } from '@/api/extensions/usePrizeOps';
import { formatCurrency } from '@/utils/formatters';

export function EventCardPrize({
  eventId,
  isDynamic,
  displayEnabled,
  staticPrize,
}: {
  eventId: string;
  isDynamic: boolean;
  displayEnabled: boolean;
  staticPrize: number;
}) {
  // Compute the live, after-adjustment total for any shown event (fixed or
  // dynamic). The static base is only a fallback while the request is in flight.
  const { data } = useEventPrize(eventId, { enabled: displayEnabled });

  // The toggle gates the prize everywhere — off shows nothing.
  if (!displayEnabled) return null;

  const computed = data?.calculation.total;
  const amount = computed ?? staticPrize;
  // A fixed event that genuinely has no prize stays hidden; a dynamic event
  // always shows (its pool grows with entries).
  if (!isDynamic && amount <= 0) return null;

  return (
    <span className="flex items-center gap-1 text-amber-600 font-medium">
      <Trophy className="h-3.5 w-3.5" />
      Prize: {formatCurrency(amount)}
    </span>
  );
}
